"use client"

import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { InfoIcon } from "lucide-react"

import type { SteelSection, SteelSectionData } from "../types/steel-section"

interface SteelSectionTableProps {
  data: SteelSectionData
}

export function SteelSectionTable({ data }: SteelSectionTableProps) {
  const [selectedWidth, setSelectedWidth] = useState<
    "narrow" | "medium" | "wide"
  >("narrow")
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  const renderTableHeader1 = () => (
    <TableHeader className="sticky top-0 bg-primary/10 z-10">
      <TableRow>
        <TableHead className="text-center font-bold" colSpan={5}>
          寸法 mm
        </TableHead>
        <TableHead className="text-center font-bold w-[60px]">断面積</TableHead>
        <TableHead className="text-center font-bold w-[80px]">
          単位質量
        </TableHead>
        <TableHead className="text-center font-bold" colSpan={2}>
          断面二次モーメント
        </TableHead>
        <TableHead className="text-center font-bold" colSpan={2}>
          断面二次半径
        </TableHead>
        <TableHead className="text-center font-bold" colSpan={2}>
          断面係数
        </TableHead>
      </TableRow>
      <TableRow>
        <TableHead className="text-center">H</TableHead>
        <TableHead className="text-center">B</TableHead>
        <TableHead className="text-center">t1</TableHead>
        <TableHead className="text-center">t2</TableHead>
        <TableHead className="text-center">r</TableHead>
        <TableHead className="text-center">cm2</TableHead>
        <TableHead className="text-center">kg/m</TableHead>
        <TableHead className="text-center">Ix cm4</TableHead>
        <TableHead className="text-center">Iy cm4</TableHead>
        <TableHead className="text-center">ix cm</TableHead>
        <TableHead className="text-center">iy cm</TableHead>
        <TableHead className="text-center">Zx cm3</TableHead>
        <TableHead className="text-center">Zy cm3</TableHead>
      </TableRow>
    </TableHeader>
  )

  const renderTableHeader2 = () => (
    <TableHeader className="sticky top-0 bg-primary/10 z-10">
      <TableRow>
        <TableHead className="text-center font-bold" colSpan={2}>
          曲げ応力のための断面性能
        </TableHead>
        <TableHead className="text-center font-bold w-[60px]" rowSpan={2}>
          鋼種
        </TableHead>
        <TableHead className="text-center font-bold w-[80px]">
          fb=ftと
        </TableHead>
        <TableHead className="text-center font-bold" colSpan={2}>
          幅厚比
        </TableHead>
        <TableHead className="text-center font-bold" colSpan={3}>
          幅厚比規定による有効断面性能
        </TableHead>
        <TableHead className="text-center font-bold" colSpan={2}>
          塑性断面係数
        </TableHead>
      </TableRow>
      <TableRow>
        <TableHead className="text-center">ib</TableHead>
        <TableHead className="text-center">η</TableHead>
        <TableHead className="text-center">なる最大横座屈長 lb(m)</TableHead>
        <TableHead className="text-center">柱</TableHead>
        <TableHead className="text-center">梁</TableHead>
        <TableHead className="text-center">Zxe cm3</TableHead>
        <TableHead className="text-center">Zye cm3</TableHead>
        <TableHead className="text-center">Ae cm2</TableHead>
        <TableHead className="text-center">Zpx cm3</TableHead>
        <TableHead className="text-center">Zpy cm3</TableHead>
      </TableRow>
    </TableHeader>
  )

  const renderTableRow1 = (section: SteelSection | null) => (
    <TableRow className="text-center hover:bg-muted/50 transition-colors">
      <TableCell>{section?.H ?? "-"}</TableCell>
      <TableCell>{section?.B ?? "-"}</TableCell>
      <TableCell>{section?.t1 ?? "-"}</TableCell>
      <TableCell>{section?.t2 ?? "-"}</TableCell>
      <TableCell>{section?.r ?? "-"}</TableCell>
      <TableCell>{section?.A ?? "-"}</TableCell>
      <TableCell>{section?.W ?? "-"}</TableCell>
      <TableCell>{section?.Ix ?? "-"}</TableCell>
      <TableCell>{section?.Iy ?? "-"}</TableCell>
      <TableCell>{section?.ix ?? "-"}</TableCell>
      <TableCell>{section?.iy ?? "-"}</TableCell>
      <TableCell>{section?.Zx ?? "-"}</TableCell>
      <TableCell>{section?.Zy ?? "-"}</TableCell>
    </TableRow>
  )

  const renderTableRow2 = (section: SteelSection | null) => (
    <>
      <TableRow className="text-center hover:bg-muted/50 transition-colors">
        <TableCell rowSpan={2}>{section?.ib ?? "-"}</TableCell>
        <TableCell rowSpan={2}>{section?.η ?? "-"}</TableCell>
        <TableCell>SN400</TableCell>
        <TableCell>{section?.fb ?? "-"}</TableCell>
        <TableCell>{section?.columnType ?? "-"}</TableCell>
        <TableCell>{section?.beamType ?? "-"}</TableCell>
        <TableCell rowSpan={2}>{section?.Zxe ?? "-"}</TableCell>
        <TableCell rowSpan={2}>{section?.Zye ?? "-"}</TableCell>
        <TableCell rowSpan={2}>{section?.Ae ?? "-"}</TableCell>
        <TableCell rowSpan={2}>{section?.Zpx ?? "-"}</TableCell>
        <TableCell rowSpan={2}>{section?.Zpy ?? "-"}</TableCell>
      </TableRow>
      <TableRow className="text-center hover:bg-muted/50 transition-colors">
        <TableCell>SN490</TableCell>
        <TableCell>{section?.fb ?? "-"}</TableCell>
        <TableCell>{section?.columnType ?? "-"}</TableCell>
        <TableCell>{section?.beamType ?? "-"}</TableCell>
      </TableRow>
    </>
  )

  const sectionList = data[selectedWidth]
  const selectedSection =
    selectedIndex !== null ? sectionList[selectedIndex] : null

  if (!isClient) {
    return null
  }

  return (
    <div className="w-full space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">H形鋼断面性能表</CardTitle>
          <CardDescription>
            この表は、H形鋼の断面性能を表示しています。適切な鋼材を選択するために使用してください。
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert className="mb-6">
            <InfoIcon className="h-4 w-4" />
            <AlertTitle>使用方法</AlertTitle>
            <AlertDescription>
              <ol className="list-decimal pl-5 space-y-2">
                <li>
                  「幅を選択」から希望の幅（細幅、中幅、広幅）を選びます。
                </li>
                <li>「H型鋼を選択」から具体的な規格を選択します。</li>
                <li>選択したH形鋼の詳細な断面性能が表示されます。</li>
              </ol>
            </AlertDescription>
          </Alert>

          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 mb-6">
            <div className="flex flex-col w-full sm:w-auto">
              <label htmlFor="widthSelect" className="text-sm font-medium mb-1">
                幅を選択
              </label>
              <Select
                value={selectedWidth}
                onValueChange={(value: "narrow" | "medium" | "wide") => {
                  setSelectedWidth(value)
                  setSelectedIndex(null)
                }}
              >
                <SelectTrigger id="widthSelect" className="w-full sm:w-[180px]">
                  <SelectValue placeholder="幅を選択" />
                </SelectTrigger>
                <SelectContent position="popper">
                  <SelectItem value="narrow">細幅</SelectItem>
                  <SelectItem value="medium">中幅</SelectItem>
                  <SelectItem value="wide">広幅</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col w-full sm:w-auto">
              <label
                htmlFor="hSteelSelect"
                className="text-sm font-medium mb-1"
              >
                H型鋼を選択
              </label>
              <Select
                value={selectedIndex !== null ? String(selectedIndex) : ""}
                onValueChange={(val) => setSelectedIndex(Number(val))}
              >
                <SelectTrigger
                  id="hSteelSelect"
                  className="w-full sm:w-[250px]"
                >
                  <SelectValue placeholder="H型鋼を選択" />
                </SelectTrigger>
                <SelectContent position="popper">
                  <ScrollArea className="h-[200px]">
                    {sectionList.map((sec, idx) => {
                      const label = `H-${sec.H}x${sec.B}x${sec.t1}x${sec.t2}`
                      return (
                        <SelectItem key={label} value={String(idx)}>
                          {label}
                        </SelectItem>
                      )
                    })}
                  </ScrollArea>
                </SelectContent>
              </Select>
            </div>
          </div>

          {selectedSection && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-6"
            >
              <Badge variant="secondary" className="text-lg font-semibold">
                選択中: H-{selectedSection.H}x{selectedSection.B}x
                {selectedSection.t1}x{selectedSection.t2}
              </Badge>
            </motion.div>
          )}

          <AnimatePresence mode="wait">
            <motion.div
              key={selectedIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-4"
            >
              <Card>
                <CardHeader>
                  <CardTitle>断面性能(1)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      {renderTableHeader1()}
                      <TableBody>{renderTableRow1(selectedSection)}</TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>断面性能(2)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      {renderTableHeader2()}
                      <TableBody>{renderTableRow2(selectedSection)}</TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </AnimatePresence>

          <Alert className="mt-6">
            <InfoIcon className="h-4 w-4" />
            <AlertTitle>注意事項</AlertTitle>
            <AlertDescription>
              <ol className="list-disc pl-5">
                <li>
                  この表のデータは参考値です。実際の設計には、最新の規格や製造元のデータを確認してください。
                </li>
                <li>
                  選択したH形鋼の使用可能性については、構造設計者や専門家に相談してください。
                </li>
                <li>
                  環境条件や荷重条件によって、適切な鋼材が異なる場合があります。
                </li>
              </ol>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  )
}
